/**
 * FeedbackReporter — header icon → file a bug or enhancement.
 *
 * Mounted in the Header (logged-in only). POSTs the Platform contract
 * (REI-API PR #28 / docs/feedback.md) to POST /api/feedback.
 * Never talks to GitHub from the SPA; does not send reporter identity.
 */

import { useState, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, Lightbulb } from 'lucide-react'
import { api } from '@/lib/api'
import { useToast } from '@/contexts/ToastContext'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/modal'
import {
  buildFeedbackIssueRequest,
  collectClientContext,
  feedbackErrorMessage,
  fileToDataUrl,
  issueUrlFromResponse,
  type FeedbackIssueType,
  type FeedbackPriority,
  type FeedbackSeverity,
  type FeedbackTarget,
} from '@/lib/feedback'

const SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const PRIORITIES: { value: FeedbackPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

const TARGETS: { value: FeedbackTarget; label: string }[] = [
  { value: 'ui', label: 'UI' },
  { value: 'api', label: 'API' },
]

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024

const fieldClass =
  'w-full rounded-lg border border-zinc-700 bg-surface-2 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25 disabled:cursor-not-allowed disabled:opacity-50'

export default function FeedbackReporter() {
  const location = useLocation()
  const { addToast } = useToast()

  const [open, setOpen] = useState(false)
  const [type, setType] = useState<FeedbackIssueType>('bug')
  const [target, setTarget] = useState<FeedbackTarget>('ui')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium')
  const [priority, setPriority] = useState<FeedbackPriority>('medium')
  const [screenshot, setScreenshot] = useState<string | undefined>()
  const [screenshotName, setScreenshotName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function resetForm() {
    setType('bug')
    setTarget('ui')
    setTitle('')
    setBody('')
    setSeverity('medium')
    setPriority('medium')
    setScreenshot(undefined)
    setScreenshotName(null)
    setError(null)
    setSubmitting(false)
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (next) {
      setError(null)
    } else {
      resetForm()
    }
  }

  async function handleScreenshot(file: File | undefined) {
    if (!file) {
      setScreenshot(undefined)
      setScreenshotName(null)
      return
    }
    if (!file.type.startsWith('image/')) {
      setError('Screenshot must be an image file.')
      return
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError('Screenshot must be 2 MB or smaller.')
      return
    }
    try {
      const dataUrl = await fileToDataUrl(file)
      setScreenshot(dataUrl)
      setScreenshotName(file.name)
      setError(null)
    } catch {
      setError('Could not read that screenshot. Try another file.')
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmedTitle = title.trim()
    const trimmedBody = body.trim()
    if (!trimmedTitle || !trimmedBody) {
      setError('Add a title and description before submitting.')
      return
    }

    setSubmitting(true)
    setError(null)

    const token = api.getToken()
    if (!token) {
      setError(feedbackErrorMessage(null, false))
      setSubmitting(false)
      return
    }

    const route = `${location.pathname}${location.search}`
    const request = buildFeedbackIssueRequest({
      title: trimmedTitle,
      body: trimmedBody,
      type,
      target,
      severity: type === 'bug' ? severity : undefined,
      priority: type === 'enhancement' ? priority : undefined,
      context: collectClientContext(route),
      screenshot,
    })

    try {
      const result = await api.createFeedbackIssue(request)
      const issueUrl = issueUrlFromResponse(result)
      addToast({
        type: 'success',
        title: type === 'bug' ? 'Bug report filed' : 'Feature request filed',
        message: issueUrl
          ? `Opened ${issueUrl}`
          : 'Thanks — the team will take a look.',
        duration: 6000,
      })
      handleOpenChange(false)
    } catch (err) {
      setError(feedbackErrorMessage(err, true))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal open={open} onOpenChange={handleOpenChange}>
        <ModalContent className="max-w-lg">
          <ModalHeader>
            <ModalTitle>Report to Forge</ModalTitle>
            <ModalDescription>
              File a bug or a feature request. We include the page you are on
              so we can reproduce it. GitHub filing is handled by the API —
              this app never stores a token or sends your identity in the body.
            </ModalDescription>
          </ModalHeader>

          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium text-zinc-100">Type</legend>
              <div className="flex gap-2" role="group" aria-label="Report type">
                <Button
                  type="button"
                  variant={type === 'bug' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('bug')}
                  aria-pressed={type === 'bug'}
                >
                  <Bug className="h-3.5 w-3.5" />
                  Bug
                </Button>
                <Button
                  type="button"
                  variant={type === 'enhancement' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('enhancement')}
                  aria-pressed={type === 'enhancement'}
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  Feature
                </Button>
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium text-zinc-100">Target</legend>
              <div className="flex gap-2" role="group" aria-label="Report target">
                {TARGETS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={target === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTarget(opt.value)}
                    aria-pressed={target === opt.value}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </fieldset>

            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                type === 'bug'
                  ? 'What went wrong?'
                  : 'What should Forge do?'
              }
              required
              maxLength={200}
            />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="feedback-body" className="text-sm font-medium text-zinc-100">
                Description
              </label>
              <textarea
                id="feedback-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                required
                placeholder={
                  type === 'bug'
                    ? 'What happened, what you expected, and how to reproduce it.'
                    : 'What you want, why it matters, and how you would use it.'
                }
                className={cn(fieldClass, 'min-h-[120px] resize-y')}
              />
            </div>

            {type === 'bug' ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="feedback-severity" className="text-sm font-medium text-zinc-100">
                  Severity
                </label>
                <select
                  id="feedback-severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as FeedbackSeverity)}
                  className={cn(fieldClass, 'h-10')}
                >
                  {SEVERITIES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="feedback-priority" className="text-sm font-medium text-zinc-100">
                  Priority
                </label>
                <select
                  id="feedback-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as FeedbackPriority)}
                  className={cn(fieldClass, 'h-10')}
                >
                  {PRIORITIES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="feedback-screenshot" className="text-sm font-medium text-zinc-100">
                Screenshot <span className="font-normal text-zinc-500">(optional)</span>
              </label>
              <input
                id="feedback-screenshot"
                type="file"
                accept="image/*"
                onChange={(e) => handleScreenshot(e.target.files?.[0])}
                className="text-sm text-zinc-400 file:mr-3 file:rounded-md file:border-0 file:bg-surface-3 file:px-3 file:py-1.5 file:text-sm file:text-zinc-100"
              />
              {screenshotName && (
                <p className="text-xs text-zinc-500">Attached: {screenshotName}</p>
              )}
            </div>

            <p className="text-xs text-zinc-500">
              Includes this page ({location.pathname || '/'}). Session is a
              bearer token only — not reporter identity. Labels are applied
              by the API from kind and target.
            </p>

            {error && (
              <p role="alert" className="text-sm text-red-400">
                {error}
              </p>
            )}

            <ModalFooter className="mt-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Submit {type === 'bug' ? 'bug' : 'feature'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        className="flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-all duration-200"
        aria-label="Report a bug or feature"
        title="Report a bug or request a feature"
      >
        <Bug className="w-4 h-4" />
      </button>
    </>
  )
}
